import { useState, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
import { Button } from '@/components/ui-kit/button';
import { MarkdownEditor, MarkdownEditorRef } from '../../../markdown-editor/markdown-editor';
import { KB, KBType } from '@/modules/gpt-chats/models/kb';
import { useUpdateKB } from '@/modules/gpt-chats/hooks/use-kb';
import { useToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { textKbFormSchema } from '@/modules/gpt-chats/components/agent-knowledge-base/text/utils';
import { MarkdownRenderer } from '@/modules/gpt-chats/components/markdown-renderer';
import { useTranslation } from 'react-i18next';

interface KBDocumentContentProps {
  kb?: KB;
}

export const KBDocumentContent = ({ kb }: KBDocumentContentProps) => {
  const { t } = useTranslation();
  const getInitialContent = (kb?: KB) => {
    if (!kb) return '';
    if (kb.kb_type === KBType.FILE) {
      return kb.file?.content || '';
    } else if (kb.kb_type === KBType.TEXT) {
      return kb.text?.content || '';
    } else if (kb.kb_type === KBType.QnA) {
      return '';
    } else if (kb.kb_type === KBType.LINK) {
      return kb.link?.content || '';
    }
    return '';
  };

  const [content, setContent] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (kb) {
      if (kb.kb_type === KBType.QnA) {
        setContent(kb.qa?.answer || '');
      } else {
        setContent(getInitialContent(kb));
      }
    }
  }, [kb]);

  const editorRef = useRef<MarkdownEditorRef>(null);
  const { toast } = useToast();

  const { mutateAsync } = useUpdateKB(kb?.kb_type || KBType.TEXT);

  const validateContent = (value: string): string | undefined => {
    textKbFormSchema.parse({ text: value });
    return undefined;
  };

  const handleContentChange = (value: string) => {
    setContent(value);
  };

  const handleSave = async () => {
    if (isSaving || !kb) return;

    const validationError = editorRef.current?.validate();
    if (validationError) {
      return;
    }

    setIsSaving(true);
    try {
      const res = await mutateAsync({
        content: content.trim(),
        type: kb.kb_type,
        kb_id: kb.id,
        question: kb.kb_type === KBType.QnA ? (kb.qa?.question as string) : '',
      });
      if (!res.is_success) {
        toast.error({ errors: res.detail });
        return;
      }
      toast.success(t('CONTENT_SUCCESSFULLY_UPDATED'));
      setIsEditing(false);
    } catch (error) {
      toast.error({ errors: ErrorTransformer(error) });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (!kb) return;
    if (kb.kb_type === KBType.QnA) {
      setContent(kb.qa?.answer || '');
    } else {
      setContent(getInitialContent(kb));
    }
    setIsEditing(false);
  };

  const handleEditStart = () => {
    setIsEditing(true);
    setTimeout(() => {
      editorRef.current?.focus();
    }, 100);
  };

  const isSaveDisabled = () => {
    if (isSaving) return true;
    if (kb?.kb_type === KBType.QnA || kb?.kb_type === KBType.TEXT) {
      const cleanLength = content.replace(/\s+/g, ' ').trim().length;
      return cleanLength < 100 || cleanLength > 10000;
    }
    return false;
  };

  if (!kb) return null;

  return (
    <Card className="flex h-[calc(100vh-16rem)] flex-col">
      <CardHeader className="flex-shrink-0 flex-row items-center justify-between border-b pb-2">
        <CardTitle className="text-xl">{t('PREVIEW')}</CardTitle>
        {!isEditing && (
          <Button type="button" variant="outline" size="sm" onClick={handleEditStart}>
            {t('EDIT')}
          </Button>
        )}
        {isEditing && (
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
               onClick={handleCancel}
              disabled={isSaving}
            >
              {t('CANCEL')}
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={() => handleSave()}
              disabled={isSaveDisabled()}
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSaving ? t('SAVING') : t('SAVE')}
            </Button>
          </div>
        )}
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden">
        {isEditing && (
          <div className="flex h-full flex-col">
            {kb.kb_type === KBType.QnA && (
              <div className="mb-4 flex-shrink-0">
                <div className="mb-1 font-medium text-high-emphasis">{t('QUESTION')}</div>
                <div className="mb-4 text-medium-emphasis">{kb.qa?.question}</div>
                <div className="mb-2 font-medium text-high-emphasis">{t('ANSWER')}</div>
              </div>
            )}

            <div className="flex-1 overflow-auto">
              <MarkdownEditor
                ref={editorRef}
                value={content}
                onChange={handleContentChange}
                minLength={
                  kb?.kb_type === KBType.QnA || kb?.kb_type === KBType.TEXT ? 100 : undefined
                }
                maxLength={
                  kb?.kb_type === KBType.QnA || kb?.kb_type === KBType.TEXT ? 10000 : undefined
                }
                showCharacterCount={true}
                validate={validateContent}
                className="h-full"
                placeholder={t('ENTER_KB_CONTENT_PLACEHOLDER')}
                disabled={isSaving}
              />
            </div>
          </div>
        )}

        {!isEditing && (
          <div className="h-full overflow-y-auto">
            {kb.kb_type === KBType.QnA ? (
              <div className="prose prose-sm max-w-none">
                <MarkdownRenderer
                  content={`${t('QUESTION_LABEL')} ${kb.qa?.question || ''}${t('ANSWER_LABEL')} ${kb.qa?.answer || ''}`}
                />
              </div>
            ) : (
              <div className="prose prose-sm max-w-none">
                <MarkdownRenderer content={content} />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
